<template>
  <section class="limiter-content pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <router-link to="/procurador/credores" class="cs-back">
      <v-icon small color="primary">mdi-chevron-left</v-icon>
      <span>Voltar para Meus credores</span>
    </router-link>

    <login-required-notice v-if="!$root.user" />
    <v-progress-linear v-else-if="loading && !data" indeterminate class="mt-6" />
    <v-alert v-else-if="error" type="error" text class="mt-6">{{ error }}</v-alert>

    <template v-else-if="data">
      <!-- Cabecalho do credor -->
      <header class="cs-hero">
        <div class="cs-hero__main">
          <div class="cs-avatar" :class="`cs-avatar--${isPj ? 'pj' : 'pf'}`">{{ initials }}</div>
          <div class="cs-hero__info">
            <span class="cs-eyebrow"><v-icon small color="primary">mdi-briefcase-account-outline</v-icon> RESUMO DO CREDOR</span>
            <h1 class="cs-name">{{ creditorName }}</h1>
            <div class="cs-meta">
              <span>{{ isPj ? "CNPJ" : "CPF" }} {{ formatRegistration(data.creditor.registration) }}</span>
              <span class="cs-chip">{{ isPj ? "Pessoa jurídica" : "Pessoa física" }}</span>
              <span class="cs-chip" :class="data.creditor.has_login ? 'cs-chip--ok' : ''">
                {{ data.creditor.has_login ? "Tem acesso à plataforma" : "Sem acesso próprio" }}
              </span>
            </div>
          </div>
        </div>
        <div class="cs-hero__actions">
          <v-btn depressed class="cs-btn cs-btn--soft" @click="$router.push(`/procurador/credores/${$route.params.id}/cadastro`)">
            <v-icon left size="18">mdi-card-account-details-outline</v-icon>Cadastro
          </v-btn>
          <v-btn depressed class="cs-btn cs-btn--primary" :disabled="!bankCases.length" @click="openBank(null)">
            <v-icon left size="18">mdi-bank-outline</v-icon>Dados bancários
          </v-btn>
        </div>
      </header>

      <div class="cs-scope">
        <v-icon small color="#7e55ab">mdi-shield-lock-outline</v-icon>
        <span>
          Você vê só os casos em que a sua procuração para este credor está aprovada
          ({{ data.cases.length }} {{ data.cases.length === 1 ? "caso" : "casos" }}).
        </span>
        <span v-if="data.other_cases.length" class="cs-scope__pending">
          Fora do resumo até a aprovação: {{ data.other_cases.map((item) => `${item.client_name} (associação ${item.status_label.toLowerCase()})`).join(", ") }}
        </span>
      </div>

      <!-- Numeros -->
      <div class="cs-stats">
        <div class="cs-stat">
          <span class="cs-stat__label">Casos</span>
          <span class="cs-stat__value">{{ data.cases.length }}</span>
        </div>
        <div class="cs-stat">
          <span class="cs-stat__label">Crédito no QGC vigente</span>
          <span class="cs-stat__value">{{ qgcTotalLabel }}</span>
        </div>
        <div class="cs-stat">
          <span class="cs-stat__label">Habilitações e divergências</span>
          <span class="cs-stat__value">{{ count("events") }}</span>
        </div>
        <div class="cs-stat">
          <span class="cs-stat__label">Credenciamentos</span>
          <span class="cs-stat__value">{{ count("accreditations") }}</span>
        </div>
      </div>

      <div v-if="data.cases.length > 1" class="cs-filter">
        <button type="button" class="cs-filter__chip" :class="{ 'cs-filter__chip--on': !caseFilter }" @click="caseFilter = null">Todos os casos</button>
        <button v-for="item in data.cases" :key="item.client_id" type="button" class="cs-filter__chip"
          :class="{ 'cs-filter__chip--on': caseFilter === item.client_id }" @click="caseFilter = item.client_id">{{ item.client_name }}</button>
      </div>

      <div class="cs-grid">
        <!-- Casos -->
        <div class="cs-cases">
          <article v-for="item in visibleCases" :key="item.client_id" class="cs-case">
            <!-- Cabecalho clicavel: abre e fecha o caso; o resumo curto fica visivel fechado -->
            <div class="cs-case__head" :class="{ 'cs-case__head--closed': !isOpen(item) }" role="button" tabindex="0"
              :aria-expanded="String(isOpen(item))" @click="toggleCase(item)" @keydown.enter.prevent="toggleCase(item)" @keydown.space.prevent="toggleCase(item)">
              <v-icon class="cs-case__chevron" :class="{ 'cs-case__chevron--closed': !isOpen(item) }">mdi-chevron-down</v-icon>
              <div class="cs-case__title">
                <div class="cs-case__name">{{ item.client_name }}</div>
                <div class="cs-case__sub">Associação aprovada em {{ item.approved_at | defaultDate("DD/MM/YYYY") }}</div>
                <div class="cs-case__chips">
                  <span class="cs-mini" :class="item.qgc.in_current ? 'cs-mini--ok' : ''">
                    <v-icon x-small>mdi-format-list-numbered</v-icon>{{ item.qgc.in_current ? caseQgcLabel(item) : "Fora do QGC vigente" }}
                  </span>
                  <span class="cs-mini" :class="item.bank ? 'cs-mini--ok' : ''">
                    <v-icon x-small>mdi-bank-outline</v-icon>{{ item.bank ? "Conta cadastrada" : "Sem conta" }}
                  </span>
                  <span class="cs-mini"><v-icon x-small>mdi-file-document-multiple-outline</v-icon>{{ item.events.length }} hab/div</span>
                  <span class="cs-mini"><v-icon x-small>mdi-account-group-outline</v-icon>{{ item.accreditations.length }} {{ item.accreditations.length === 1 ? "credenciamento" : "credenciamentos" }}</span>
                </div>
              </div>
              <v-btn text small class="cs-link" @click.stop="$router.push(`/procurador/casos/${item.client_id}/assembleia`)">
                Assembleia <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </div>

            <v-expand-transition>
            <div v-show="isOpen(item)">
            <!-- QGC -->
            <div class="cs-block">
              <div class="cs-block__title"><v-icon small>mdi-format-list-numbered</v-icon> QGC vigente</div>
              <div v-if="item.qgc.in_current" class="cs-credits">
                <div v-for="credit in item.qgc.credits" :key="credit.id" class="cs-credit">
                  <span>{{ credit.class_name }}</span>
                  <strong>{{ money(credit.amount, credit.currency) }}</strong>
                </div>
              </div>
              <div v-else class="cs-empty">O credor não consta no QGC vigente deste caso.</div>
            </div>

            <!-- Dados bancarios -->
            <div class="cs-block">
              <div class="cs-block__title">
                <v-icon small>mdi-bank-outline</v-icon> Dados bancários
                <v-spacer />
                <v-btn v-if="item.bank_enabled" text small class="cs-link" @click="openBank(item.client_id)">
                  {{ item.bank ? "Alterar" : "Cadastrar" }}
                </v-btn>
              </div>
              <div v-if="item.bank" class="cs-bank">
                <div class="cs-bank__holder">
                  <span class="cs-tag" :class="item.bank.holder === 'procurator' ? 'cs-tag--lav' : ''">
                    {{ item.bank.holder === "procurator" ? "Conta do procurador" : "Conta do credor" }}
                  </span>
                  <span class="text-capitalize">{{ String(item.bank.holder_name || "").toLowerCase() }}</span>
                </div>
                <div class="cs-bank__line">
                  {{ item.bank.bank_code }} · {{ item.bank.bank_name }} · Ag. {{ item.bank.agency }} ·
                  {{ item.bank.account_type_label || "Conta" }} {{ item.bank.account_number }}{{ item.bank.account_digit ? `-${item.bank.account_digit}` : "" }}
                </div>
                <div class="cs-bank__line cs-bank__line--muted">
                  Titular {{ formatRegistration(item.bank.registration) }}
                  <template v-if="item.bank.pix_key"> · PIX {{ pixLabel(item.bank.pix_type) }} {{ item.bank.pix_key }}</template>
                  · atualizado em {{ item.bank.updated_at | defaultDate("DD/MM/YYYY") }}
                </div>
              </div>
              <div v-else-if="item.bank_enabled" class="cs-empty">Nenhuma conta cadastrada para este caso.</div>
              <div v-else class="cs-empty">O envio de dados bancários está fechado neste caso.</div>
            </div>

            <!-- Hab/div -->
            <div class="cs-block">
              <div class="cs-block__title"><v-icon small>mdi-file-document-multiple-outline</v-icon> Habilitações e divergências</div>
              <div v-if="item.events.length" class="cs-items">
                <div v-for="event in item.events" :key="event.id" class="cs-item">
                  <div>
                    <div class="cs-item__title">{{ event.type_label }} nº {{ event.id }}</div>
                    <div class="cs-item__sub">Enviada em {{ event.created_at | defaultDate("DD/MM/YYYY") }}</div>
                  </div>
                  <span class="cs-pill" :class="`cs-pill--${eventTone(event.status)}`">{{ event.status_label }}</span>
                </div>
              </div>
              <div v-else class="cs-empty">Nenhum pedido neste caso.</div>
            </div>

            <!-- Credenciamentos -->
            <div class="cs-block cs-block--last">
              <div class="cs-block__title"><v-icon small>mdi-account-group-outline</v-icon> Credenciamentos</div>
              <div v-if="item.accreditations.length" class="cs-items">
                <div v-for="row in item.accreditations" :key="row.id" class="cs-item">
                  <div>
                    <div class="cs-item__title">{{ row.session_label }} · {{ row.meeting_date | defaultDate("DD/MM/YYYY") }}</div>
                    <div class="cs-item__sub">
                      {{ row.participation_label }}<template v-if="row.represented_by"> · representado por <span class="text-capitalize">{{ String(row.represented_by).toLowerCase() }}</span></template>
                    </div>
                  </div>
                  <span class="cs-pill" :class="`cs-pill--${accreditationTone(row.status)}`">{{ row.status_label }}</span>
                </div>
              </div>
              <div v-else class="cs-empty">Nenhum credenciamento neste caso.</div>
            </div>
            </div>
            </v-expand-transition>
          </article>
        </div>

        <!-- Lateral: linha do tempo e documentos -->
        <aside class="cs-side">
          <section class="cs-panel">
            <div class="cs-panel__title">Linha do tempo</div>
            <ol v-if="visibleTimeline.length" class="cs-timeline">
              <li v-for="(entry, index) in visibleTimeline" :key="index" class="cs-timeline__item">
                <span class="cs-timeline__dot" :class="`cs-timeline__dot--${entry.kind}`">
                  <v-icon x-small color="white">{{ timelineIcon(entry.kind) }}</v-icon>
                </span>
                <div class="cs-timeline__body">
                  <div class="cs-timeline__title">{{ entry.title }}</div>
                  <div class="cs-timeline__sub">
                    {{ entry.date | defaultDate("DD/MM/YYYY") }}
                    <template v-if="entry.client_name && !caseFilter"> · {{ entry.client_name }}</template>
                    <template v-if="entry.detail"> · {{ entry.detail }}</template>
                  </div>
                </div>
              </li>
            </ol>
            <div v-else class="cs-empty">Sem movimentações.</div>
          </section>

          <section class="cs-panel">
            <div class="cs-panel__title">Documentos</div>
            <div v-if="visibleDocuments.length" class="cs-docs">
              <a v-for="doc in visibleDocuments" :key="doc.key" class="cs-doc" :class="{ 'cs-doc--off': !doc.url }" @click.prevent="openDocument(doc.url)">
                <span class="cs-doc__icon"><v-icon small color="#7e55ab">mdi-file-document-outline</v-icon></span>
                <span class="cs-doc__info">
                  <span class="cs-doc__name">{{ doc.type_label }}</span>
                  <span class="cs-doc__sub">
                    {{ doc.client_name || "Cadastro do credor" }} · {{ doc.created_at | defaultDate("DD/MM/YYYY") }}
                    <template v-if="doc.status !== null"> · {{ docStatus(doc.status) }}</template>
                  </span>
                </span>
                <v-icon small color="#9a93a6">mdi-open-in-new</v-icon>
              </a>
            </div>
            <div v-else class="cs-empty">Nenhum documento.</div>
          </section>
        </aside>
      </div>
    </template>

    <procurator-bank-dialog v-model="bankDialog.show" :association-id="$route.params.id" :client-id="bankDialog.clientId" @saved="data = $event" />
  </section>
</template>

<script>
// Resumo do credor representado: so os casos com associacao aprovada deste procurador.
import axios from "axios";
import pixKeyTypes from "@/common/data/pix-key-types.data";
import ProcuratorBankDialog from "@/components/ProcuratorBankDialog.vue";

const CURRENCIES = { 1: "BRL", 2: "USD", 3: "EUR" };

export default {
  name: "ProcuratorCreditorSummary",

  components: { ProcuratorBankDialog },

  data: () => ({
    loading: false,
    error: "",
    data: null,
    caseFilter: null,
    // Casos abertos (client_id -> true); o primeiro abre ao carregar.
    openCases: {},
    bankDialog: { show: false, clientId: null },
  }),

  computed: {
    isPj() {
      return !!this.data && (this.data.creditor.person_type === "pj" || String(this.data.creditor.registration || "").length === 14);
    },
    creditorName() {
      return String(this.data.creditor.name || "").toLowerCase().replace(/(^|\s)\S/g, (char) => char.toUpperCase());
    },
    initials() {
      const parts = String(this.data.creditor.name || "").trim().split(/\s+/).filter(Boolean);
      return ((parts[0] || "")[0] + ((parts.length > 1 ? parts[parts.length - 1] : "")[0] || "")).toUpperCase();
    },
    visibleCases() {
      return this.caseFilter ? this.data.cases.filter((item) => item.client_id === this.caseFilter) : this.data.cases;
    },
    bankCases() {
      return this.data.cases.filter((item) => item.bank_enabled);
    },
    visibleTimeline() {
      if (!this.caseFilter) return this.data.timeline;
      const name = (this.data.cases.find((item) => item.client_id === this.caseFilter) || {}).client_name;
      return this.data.timeline.filter((entry) => !entry.client_name || entry.client_name === name);
    },
    visibleDocuments() {
      if (!this.caseFilter) return this.data.documents;
      const name = (this.data.cases.find((item) => item.client_id === this.caseFilter) || {}).client_name;
      return this.data.documents.filter((doc) => !doc.client_name || doc.client_name === name);
    },
    qgcTotalLabel() {
      const totals = {};
      this.visibleCases.forEach((item) => item.qgc.total_by_currency.forEach((row) => { totals[row.currency] = (totals[row.currency] || 0) + row.amount; }));
      const entries = Object.entries(totals);
      return entries.length ? entries.map(([currency, amount]) => this.money(amount, Number(currency))).join(" + ") : "—";
    },
  },

  created() {
    if (this.$root.user) this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = "";
      try {
        const result = await axios.get(`/api/procurators/creditors/${this.$route.params.id}/summary`);
        this.data = result.data.content;
        const requested = Number(this.$route.query.client_id);
        if (requested && this.data.cases.some((item) => item.client_id === requested)) this.caseFilter = requested;
        const first = requested || (this.data.cases[0] && this.data.cases[0].client_id);
        this.openCases = first ? { [first]: true } : {};
        if (this.$route.query.bank) this.openBank(requested || null);
      } catch (error) {
        this.error = this.$codes(error);
      } finally {
        this.loading = false;
      }
    },

    isOpen(item) {
      return !!this.openCases[item.client_id];
    },

    toggleCase(item) {
      this.$set(this.openCases, item.client_id, !this.openCases[item.client_id]);
    },

    caseQgcLabel(item) {
      return item.qgc.total_by_currency.map((row) => this.money(row.amount, row.currency)).join(" + ") || "No QGC vigente";
    },

    count(key) {
      return this.visibleCases.reduce((total, item) => total + item[key].length, 0);
    },

    openBank(clientId) {
      this.bankDialog = { show: true, clientId: clientId || this.caseFilter };
    },

    openDocument(url) {
      if (url) window.open(url, "_blank");
    },

    money(amount, currency) {
      return new Intl.NumberFormat("pt-BR", { style: "currency", currency: CURRENCIES[currency] || "BRL" }).format(amount || 0);
    },

    formatRegistration(value) {
      const digits = String(value || "").replace(/\D/g, "");
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
      if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
      return digits || "—";
    },

    pixLabel(type) {
      const item = pixKeyTypes.find((row) => row.id === Number(type));
      return item ? item.name : "";
    },

    eventTone(status) {
      return { 0: "warn", 1: "ok", 2: "danger", 3: "ok", 4: "alert", 5: "info" }[Number(status)] || "info";
    },

    accreditationTone(status) {
      return { 0: "warn", 1: "ok", 2: "danger" }[Number(status)] || "info";
    },

    docStatus(status) {
      return { 0: "em análise", 1: "aprovado", 2: "recusado" }[Number(status)] || "em análise";
    },

    timelineIcon(kind) {
      return {
        association: "mdi-link-variant",
        qgc: "mdi-format-list-numbered",
        bank: "mdi-bank-outline",
        event: "mdi-file-document-outline",
        accreditation: "mdi-account-group-outline",
        document: "mdi-paperclip",
      }[kind] || "mdi-circle-small";
    },
  },
};
</script>

<style scoped>
.cs-back { display: inline-flex; align-items: center; gap: 2px; margin-top: 12px; padding: 4px 10px 4px 4px; border-radius: 6px; color: var(--v-primary-base); font-size: 14px; font-weight: 500; text-decoration: none; }
.cs-back:hover { background: rgba(0, 0, 0, 0.04); }

.cs-hero { display: flex; align-items: center; justify-content: space-between; gap: 24px; flex-wrap: wrap; margin: 32px 0 28px; }
.cs-hero__main { display: flex; align-items: center; gap: 22px; min-width: 0; }
.cs-avatar { flex: 0 0 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 24px; font-weight: 700; }
.cs-avatar--pf { background: rgba(120, 119, 229, 0.14); color: #4b3a8f; }
.cs-avatar--pj { background: rgba(170, 211, 199, 0.45); color: #1f5b4b; }
.cs-hero__info { min-width: 0; }
.cs-eyebrow { display: flex; gap: 8px; align-items: center; font-size: 11px; font-weight: 700; letter-spacing: 0.12em; color: #766386; }
.cs-name { margin: 10px 0 10px; font-size: clamp(26px, 3vw, 36px); font-weight: 700; color: #25113c; letter-spacing: -0.03em; line-height: 1.15; }
.cs-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; font-size: 14px; color: #73677c; }
.cs-chip { padding: 3px 12px; border-radius: 999px; background: #f3f0f7; font-size: 12px; font-weight: 600; color: #5a4d6d; }
.cs-chip--ok { background: #e7f6ee; color: #1e7b4f; }
.cs-hero__actions { display: flex; gap: 10px; flex-wrap: wrap; }

.cs-btn { height: 44px !important; padding: 0 20px !important; border-radius: 10px !important; text-transform: none !important; letter-spacing: 0 !important; font-size: 14px !important; font-weight: 600 !important; }
.cs-btn--primary { background: #1d0b33 !important; color: #fff !important; }
.cs-btn--primary.v-btn--disabled { background: #e7e3ed !important; color: #9a93a6 !important; }
.cs-btn--soft { background: rgba(126, 85, 171, 0.12) !important; color: #4b2a75 !important; }
.cs-link { text-transform: none !important; letter-spacing: 0 !important; font-weight: 600 !important; color: #7e55ab !important; }

.cs-scope { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; padding: 14px 18px; border-radius: 12px; background: #f6f4f9; font-size: 13.5px; color: #4a4257; line-height: 1.5; }
.cs-scope__pending { flex-basis: 100%; padding-left: 24px; color: #a16207; }

.cs-stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; margin: 28px 0; }
.cs-stat { display: flex; flex-direction: column; gap: 8px; padding: 20px 22px; border: 1px solid #ece8f2; border-radius: 14px; background: #fff; }
.cs-stat__label { font-size: 12.5px; font-weight: 600; color: #73677c; }
.cs-stat__value { font-size: 22px; font-weight: 700; color: #1d0b33; }

.cs-filter { display: flex; flex-wrap: wrap; gap: 8px; margin: -4px 0 24px; }
.cs-filter__chip { padding: 8px 16px; border-radius: 999px; border: 1px solid #ddd5e8; background: #fff; font-size: 13px; font-weight: 600; color: #4b2a75; }
.cs-filter__chip--on { background: #1d0b33; border-color: #1d0b33; color: #fff; }

.cs-grid { display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 28px; align-items: start; margin-bottom: 56px; }
.cs-cases { display: flex; flex-direction: column; gap: 24px; }
.cs-case { border: 1px solid #ece8f2; border-radius: 16px; background: #fff; padding: 8px 28px 10px; }
.cs-case__head { display: flex; align-items: flex-start; gap: 14px; padding: 20px 0 18px; border-bottom: 1px solid #f0edf4; cursor: pointer; outline: none; }
.cs-case__head--closed { border-bottom-color: transparent; padding-bottom: 12px; }
.cs-case__head:focus-visible .cs-case__name { text-decoration: underline; }
.cs-case__chevron { margin-top: 1px; color: #7e55ab !important; transition: transform 0.2s ease; }
.cs-case__chevron--closed { transform: rotate(-90deg); }
.cs-case__title { flex: 1; min-width: 0; }
.cs-case__chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.cs-mini { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border-radius: 999px; background: #f3f0f7; font-size: 12px; font-weight: 600; color: #5a4d6d; }
.cs-mini .v-icon { color: inherit !important; }
.cs-mini--ok { background: #e7f6ee; color: #1e7b4f; }
.cs-case__name { font-size: 17px; font-weight: 700; color: #1d0b33; }
.cs-case__sub { margin-top: 4px; font-size: 13px; color: #73677c; }
.cs-block { padding: 22px 0; border-bottom: 1px solid #f0edf4; }
.cs-block--last { border-bottom: 0; }
.cs-block__title { display: flex; align-items: center; gap: 10px; min-height: 28px; margin-bottom: 14px; font-size: 13px; font-weight: 700; color: #5a4d6d; text-transform: uppercase; letter-spacing: 0.04em; }
.cs-block__title .v-icon { color: #7e55ab; }
.cs-empty { font-size: 14px; color: #8c8597; line-height: 1.5; }

.cs-credits { display: flex; flex-direction: column; gap: 10px; }
.cs-credit { display: flex; justify-content: space-between; gap: 16px; padding: 14px 16px; border-radius: 12px; background: #faf9fc; font-size: 14px; color: #2a1a40; }

.cs-bank { padding: 16px 18px; border-radius: 12px; background: #faf9fc; }
.cs-bank__holder { display: flex; align-items: center; gap: 10px; font-size: 14.5px; font-weight: 600; color: #2a1a40; }
.cs-bank__line { margin-top: 8px; font-size: 14px; color: #2a1a40; }
.cs-bank__line--muted { margin-top: 6px; color: #73677c; font-size: 13px; }
.cs-tag { padding: 3px 10px; border-radius: 6px; background: #e7f6ee; color: #1e7b4f; font-size: 11.5px; font-weight: 700; }
.cs-tag--lav { background: rgba(126, 85, 171, 0.12); color: #4b2a75; }

.cs-items { display: flex; flex-direction: column; gap: 10px; }
.cs-item { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 14px 16px; border-radius: 12px; background: #faf9fc; }
.cs-item__title { font-size: 14px; font-weight: 600; color: #2a1a40; }
.cs-item__sub { margin-top: 3px; font-size: 13px; color: #73677c; }
.cs-pill { flex: 0 0 auto; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.cs-pill--ok { background: #e7f6ee; color: #1e7b4f; }
.cs-pill--warn { background: #fff4dc; color: #a16207; }
.cs-pill--danger { background: #fdecea; color: #b42318; }
.cs-pill--alert { background: #fff1e8; color: #c2410c; }
.cs-pill--info { background: #eef2ff; color: #3949ab; }

.cs-side { display: flex; flex-direction: column; gap: 24px; position: sticky; top: 24px; }
.cs-panel { border: 1px solid #ece8f2; border-radius: 16px; background: #fff; padding: 22px 24px; }
.cs-panel__title { margin-bottom: 18px; font-size: 16px; font-weight: 700; color: #1d0b33; }

.cs-timeline { list-style: none; margin: 0; padding: 0; max-height: 480px; overflow-y: auto; }
.cs-timeline__item { position: relative; display: flex; gap: 14px; padding-bottom: 20px; }
.cs-timeline__item:last-child { padding-bottom: 0; }
.cs-timeline__item:not(:last-child)::before { content: ""; position: absolute; left: 13px; top: 30px; bottom: 4px; width: 2px; background: #ece8f2; }
.cs-timeline__dot { flex: 0 0 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #7e55ab; }
.cs-timeline__dot--qgc { background: #3949ab; }
.cs-timeline__dot--bank { background: #1e7b4f; }
.cs-timeline__dot--event { background: #c2410c; }
.cs-timeline__dot--accreditation { background: #1d0b33; }
.cs-timeline__dot--document { background: #8c8597; }
.cs-timeline__body { padding-top: 3px; }
.cs-timeline__title { font-size: 14px; font-weight: 600; color: #2a1a40; line-height: 1.35; }
.cs-timeline__sub { margin-top: 3px; font-size: 12.5px; color: #73677c; line-height: 1.45; }

.cs-docs { display: flex; flex-direction: column; gap: 6px; }
.cs-doc { display: flex; align-items: center; gap: 14px; padding: 12px; margin: 0 -12px; border-radius: 12px; text-decoration: none; transition: background 0.15s; cursor: pointer; }
.cs-doc:hover { background: #faf9fc; }
.cs-doc--off { opacity: 0.55; pointer-events: none; }
.cs-doc__icon { flex: 0 0 38px; height: 38px; border-radius: 10px; background: rgba(126, 85, 171, 0.1); display: flex; align-items: center; justify-content: center; }
.cs-doc__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.cs-doc__name { font-size: 14px; font-weight: 600; color: #2a1a40; }
.cs-doc__sub { font-size: 12.5px; color: #73677c; line-height: 1.45; }

@media (max-width: 1100px) {
  .cs-grid { grid-template-columns: 1fr; }
  .cs-side { position: static; }
}
@media (max-width: 700px) {
  .cs-hero { margin: 24px 0 20px; }
  .cs-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .cs-stat { padding: 16px; }
  .cs-case { padding: 6px 16px 8px; }
  .cs-panel { padding: 18px; }
}
</style>
